/**
 * Servicio para manejar operaciones relacionadas con distancias entre patios.
 *
 * Endpoint base:
 *   /patios/distancias/
 *
 * Funcionalidades:
 * - Listar distancias registradas (GET)
 * - Crear nueva distancia (POST)
 * - Actualizar distancia existente (PATCH, requiere ID)
 * - Eliminar distancia (DELETE, opcional)
 */

import { API_BASE_URL } from '../config/config';
import axios from './authService';

// 📍 ENDPOINTS ESPECÍFICOS DE DISTANCIAS ENTRE PATIOS
const DISTANCIAS_ENDPOINTS = {
  LIST: '/api/patios/distancias/',   // para GET y POST
  DETAIL: '/api/patios/distancias',  // para /patios/distancias/:id/
//IMPORTANTE la ruta real es /patios/distancias/ solo para host4 es lleva el /api/...
};

// ⚙️ CONFIGURACIÓN LOCAL DEL SERVICIO
const SERVICE_CONFIG = {
  timeout: 10000, // 10 segundos
  headers: {
    'Content-Type': 'application/json',
  },
  enableApiLogs: true,
  enableErrorLogs: true,
  enableSuccessLogs: true,
};

/**
 * Construye URL completa para un endpoint específico
 * @param {string} endpoint - Endpoint relativo, ej: '/patios/distancias/' o '/patios/distancias/1/'
 * @returns {string} URL completa
 */
const buildUrl = (endpoint) => {
  const baseUrl = API_BASE_URL.endsWith('/')
    ? API_BASE_URL.slice(0, -1)
    : API_BASE_URL;

  const cleanEndpoint = endpoint.startsWith('/')
    ? endpoint
    : `/${endpoint}`;

  return `${baseUrl}${cleanEndpoint}`;
};

/**
 * GET: Obtiene todas las distancias registradas
 *
 * @returns {Promise<Array>} Lista de distancias
 * 
 * Estructura esperada de cada item (ejemplo):
 * {
 *   id: number,
 *   origen: number,        // ID de patio origen
 *   destino: number,       // ID de patio destino
 *   distancia_km: string   // Distancia en km (string)
 *   // ... otros campos que el API devuelva
 * }
 */
export const obtenerDistancias = async () => {
  try {
    const url = buildUrl(DISTANCIAS_ENDPOINTS.LIST);

    if (SERVICE_CONFIG.enableApiLogs) {
      console.log('📡 GET distancias:', url);
    }

    const response = await axios.get(url, {
      headers: {
        ...SERVICE_CONFIG.headers,
      },
      timeout: SERVICE_CONFIG.timeout,
    });

    const data = response.data;

    if (SERVICE_CONFIG.enableSuccessLogs) {
      console.log('✅ Distancias obtenidas:', data);
    }

    // Por si usan formato { results: [...] }
    if (Array.isArray(data)) {
      return data;
    }
    return data.results || [];
  } catch (error) {

  if (SERVICE_CONFIG.enableErrorLogs) {
        console.error('🔥 Error en obtenerDistancias:', error);
        if (error.response) {
          console.error('   Status:', error.response.status);
          console.error('   Data:', error.response.data);
        }
    }
    
    const msg =
      error.response?.data?.detail ||
      error.message ||
      'Error desconocido al cargar distancias';

    throw new Error(`Error al cargar distancias: ${msg}`);
  }
};

/**
 * POST: Crea una nueva distancia entre patios
 *
 * @param {Object} distanciaData
 * @param {number} distanciaData.origen   - ID del patio origen
 * @param {number} distanciaData.destino  - ID del patio destino
 * @param {string|number} distanciaData.distancia_km - Distancia en km
 *
 * Ejemplo de payload:
 * {
 *   "origen": 13,
 *   "destino": 18,
 *   "distancia_km": "10"
 * }
 */
export const crearDistancia = async (distanciaData) => {
  try {
    const url = buildUrl(DISTANCIAS_ENDPOINTS.LIST);

    const payload = {
      origen: distanciaData.origen,
      destino: distanciaData.destino,
      // Normalizamos a string por si mandas número
      distancia_km: String(distanciaData.distancia_km),
    };

    if (SERVICE_CONFIG.enableApiLogs) {
      console.log('📡 POST distancias:', url);
      console.log('📦 Payload:', payload);
    }

    const response = await axios.post(url, payload, {
      headers: {
        ...SERVICE_CONFIG.headers,
      },
      timeout: SERVICE_CONFIG.timeout,
    });

    const data = response.data;

    if (SERVICE_CONFIG.enableSuccessLogs) {
      console.log('✅ Distancia creada:', data);
    }

    return data;
  } catch (error) {
    if (SERVICE_CONFIG.enableErrorLogs) {
      console.error('🔥 Error en crearDistancia:', error);
      if (error.response) {
        console.error('   Status:', error.response.status);
        console.error('   Data:', error.response.data);
      }
    }

    const msg =
      error.response?.data?.detail ||
      error.message ||
      'Error desconocido al crear distancia';

    throw new Error(`Error al crear distancia: ${msg}`);
  }
};

/**
 * PATCH: Actualiza una distancia existente (resource)
 *
 * NOTA: el dev te dijo que para modificar hay que mandar el ID,
 * ej: PATCH /patios/distancias/5/
 *
 * @param {number|string} id - ID del registro de distancia
 * @param {Object} distanciaData - Campos a actualizar
 * @param {number} [distanciaData.origen]
 * @param {number} [distanciaData.destino]
 * @param {string|number} [distanciaData.distancia_km]
 */
export const actualizarDistancia = async (id, distanciaData) => {
  try {
    // Usamos DETAIL (sin slash) y agregamos /id/
    const endpoint = `${DISTANCIAS_ENDPOINTS.DETAIL}/${id}/`;
    const url = buildUrl(endpoint);

    const payload = {};

    if (distanciaData.origen != null) payload.origen = distanciaData.origen;
    if (distanciaData.destino != null) payload.destino = distanciaData.destino;
    if (distanciaData.distancia_km != null) {
      payload.distancia_km = String(distanciaData.distancia_km);
    }

    if (SERVICE_CONFIG.enableApiLogs) {
      console.log('📡 PATCH distancia:', url);
      console.log('📦 Payload (update):', payload);
    }

    const response = await axios.patch(url, payload, {
      headers: {
        ...SERVICE_CONFIG.headers,
      },
      timeout: SERVICE_CONFIG.timeout,
    });

    const data = response.data;

    if (SERVICE_CONFIG.enableSuccessLogs) {
      console.log('✅ Distancia actualizada:', data);
    }

    return data;
  } catch (error) {
    if (SERVICE_CONFIG.enableErrorLogs) {
      console.error('🔥 Error en actualizarDistancia:', error);
      if (error.response) {
        console.error('   Status:', error.response.status);
        console.error('   Data:', error.response.data);
      }
    }

    const msg =
      error.response?.data?.detail ||
      error.message ||
      'Error desconocido al actualizar distancia';

    throw new Error(`Error al actualizar distancia: ${msg}`);
  }
};

/**
 * DELETE: Elimina una distancia por ID (si lo llegas a usar)
 * @param {number|string} id
 */
export const eliminarDistancia = async (id) => {
  try {
    const endpoint = `${DISTANCIAS_ENDPOINTS.DETAIL}/${id}/`;
    const url = buildUrl(endpoint);

    if (SERVICE_CONFIG.enableApiLogs) {
      console.log('📡 DELETE distancia:', url);
    }

    const response = await axios.delete(url, {
      headers: {
        ...SERVICE_CONFIG.headers,
      },
      timeout: SERVICE_CONFIG.timeout,
    });

    if (SERVICE_CONFIG.enableSuccessLogs) {
      console.log('✅ Distancia eliminada, status:', response.status);
    }

    return { success: true, status: response.status };
  } catch (error) {
    if (SERVICE_CONFIG.enableErrorLogs) {
      console.error('🔥 Error en eliminarDistancia:', error);
      if (error.response) {
        console.error('   Status:', error.response.status);
        console.error('   Data:', error.response.data);
      }
    }

    const msg =
      error.response?.data?.detail ||
      error.message ||
      'Error desconocido al eliminar distancia';

    throw new Error(`Error al eliminar distancia: ${msg}`);
  }
};

/**
 * 🧪 Función de prueba de conexión específica para distancias
 */
export const testConnectionDistancias = async () => {
  try {
    const url = buildUrl(DISTANCIAS_ENDPOINTS.LIST);
    console.log(`🧪 Probando conexión a distancias: ${url}`);

    const response = await fetch(url, {
      method: 'GET',
      headers: SERVICE_CONFIG.headers,
      signal: AbortSignal.timeout(SERVICE_CONFIG.timeout),
    });

    if (response.ok) {
      console.log('✅ Conexión exitosa con API de distancias');
      return { success: true, status: response.status };
    } else {
      console.log(`❌ Error de conexión distancias: ${response.status}`);
      return {
        success: false,
        status: response.status,
        error: response.statusText,
      };
    }
  } catch (error) {
    console.error('🔥 Error al probar conexión (distancias):', error);
    return { success: false, error: error.message };
  }
};

/**
 * Configurar el servicio dinámicamente
 * @param {Object} config
 */
export const configurarServicioDistancias = (config) => {
  if (config.timeout) SERVICE_CONFIG.timeout = config.timeout;
  if (config.enableApiLogs !== undefined)
    SERVICE_CONFIG.enableApiLogs = config.enableApiLogs;
  if (config.enableErrorLogs !== undefined)
    SERVICE_CONFIG.enableErrorLogs = config.enableErrorLogs;
  if (config.enableSuccessLogs !== undefined)
    SERVICE_CONFIG.enableSuccessLogs = config.enableSuccessLogs;
  if (config.headers)
    SERVICE_CONFIG.headers = { ...SERVICE_CONFIG.headers, ...config.headers };

  console.log('🔧 Servicio de distancias reconfigurado:', SERVICE_CONFIG);
};

// 📊 Info útil para debugging
export const getServiceInfoDistancias = () => ({
  baseUrl: API_BASE_URL,
  endpoints: DISTANCIAS_ENDPOINTS,
  config: SERVICE_CONFIG,
  availableActions: [
    'obtenerDistancias',
    'crearDistancia',
    'actualizarDistancia',
    'eliminarDistancia',
    'testConnectionDistancias',
  ],
});

// 📋 Exportar endpoints si los necesitas fuera
export const ENDPOINTS_DISTANCIAS = DISTANCIAS_ENDPOINTS;
